import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  ReplaySubject,
  combineLatest,
  filter,
  first,
  map,
  pipe,
  pluck,
  shareReplay,
  switchMap,
} from 'rxjs';

import { cloneDeep } from 'lodash-es';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  CommentContext,
  CommentsByMeInMonthDocument,
  CommentsByMeInMonthQuery,
  CommentsByMeInMonthQueryVariables,
  CommnetType,
  GetCommentsDocument,
  GetCommentsGQL,
  GetCommentsQuery,
  GetCommentsQueryVariables,
  GetConversationStatusGQL,
  InsertCommentGQL,
  UpdateSeenCommentsGQL,
  WatchForNewCommentsDocument,
  WatchForNewCommentsGQL,
  WatchForNewCommentsSubscription,
  WatchForNewCommentsSubscriptionVariables,
} from 'ZalamoLibrary/src/lib/ui/comments/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';

import cuid from 'cuid/index';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';

import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { Apollo } from 'apollo-angular';
import { UserDetailsService } from 'ZalamoLibrary/src/lib/zalamo/auth/user-details.service';
import { CommentsService } from 'ZalamoLibrary/src/lib/ui/comments/comments.service';
import { DateTime } from 'luxon';

@Component({
  selector: 'lib-comments-container',
  templateUrl: './comments-container.component.html',
  styleUrls: ['./comments-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommentsContainerComponent extends AliveState implements OnInit {
  @Output() draftDirtyChange = new EventEmitter<boolean>();
  context$ = new ReplaySubject<CommentContext>(1);
  contextId$ = new ReplaySubject<string>(1);
  conversationStatus$ = combineLatest([this.context$, this.contextId$]).pipe(
    switchMap(([context, contextId]) =>
      this.getConversationStatusGQL
        .fetch({
          variables: {
            context,
            contextId,
          },
        })
        .pipe(pluck('data', 'getConversationStatus')),
    ),
  );
  commentsCreatedByMe: Array<string> = [];
  conversation$ = this.conversationStatus$.pipe(
    switchMap((conversationStatus) =>
      combineLatest([this.context$, this.contextId$]).pipe(
        switchMap(([context, contextId]) => {
          const query = this.getCommentsGQL.watch({
            ...{
              fetchPolicy:
                conversationStatus?.isSeen === true || !conversationStatus
                  ? 'cache-first'
                  : 'cache-and-network',
            },
            variables: {
              context,
              contextId,
            },
          });

          query.subscribeToMore<
            WatchForNewCommentsSubscription,
            WatchForNewCommentsSubscriptionVariables
          >({
            document: WatchForNewCommentsDocument,
            variables: {
              context,
              contextId,

              watcherId: this.userDetailsService.getUserId(),
            },
            updateQuery: (
              _previousQueryResult,
              { subscriptionData, complete, previousData },
            ) => {
              if (!complete) {
                return undefined;
              }
              const previousQueryResult = previousData;
              if (
                !subscriptionData.data ||
                this.commentsCreatedByMe.includes(
                  subscriptionData.data.newCommentAdded?.commentId,
                )
              ) {
                return previousQueryResult;
              }
              const newFeedItem = subscriptionData.data.newCommentAdded;
              return {
                ...previousQueryResult,
                getComments: {
                  ...previousQueryResult.getComments,
                  comments: [
                    ...previousQueryResult.getComments.comments,
                    newFeedItem,
                  ],
                },
              };
            },
          });

          return query.valueChanges.pipe(
            throwOnQueryError,
            filter(hasCompleteQueryData),
            map((result) => result.data['getComments']),
          );
        }),
      ),
    ),
  );
  commentsList$ = this.conversation$.pipe(
    pluck('comments'),
    map((comments) => [...comments].sort((a, b) => a.date - b.date)),
  );
  isSolo$ = this.commentsList$.pipe(
    map(
      (comments) =>
        [...new Set(comments.map(({ authorId }) => authorId))].length <= 1,
    ),
  );
  conversationId$ = this.conversation$.pipe(
    pluck('commentsListId'),
    shareReplay(),
  );

  private _context;

  @Input() @Required set context(context: CommentContext) {
    this._context = context;
    this.context$.next(context);
  }

  private _contextId;

  @Input() @Required set contextId(contextId: string) {
    this._contextId = contextId;

    this.contextId$.next(contextId);
  }

  constructor(
    private getCommentsGQL: GetCommentsGQL,
    private insertCommentGQL: InsertCommentGQL,
    public organizationService: OrganizationService,
    private watchForNewCommentsGQL: WatchForNewCommentsGQL,
    private apollo: Apollo,
    private userDetailsService: UserDetailsService,
    private commentsService: CommentsService,
    private getConversationStatusGQL: GetConversationStatusGQL,
    private updateSeenCommentsGQL: UpdateSeenCommentsGQL,
  ) {
    super();
  }

  ngOnInit(): void {}

  parseComment(comment: string) {
    const helper = document.createElement('div');
    helper.innerHTML = comment;
    const mentions = helper.querySelectorAll('span[class="mention"]');
    console.log(mentions);

    return {
      text: comment,
      mentions: Array.from(mentions).map((mention) =>
        mention.getAttribute('data-id'),
      ),
    };
  }

  addComment(comment: string) {
    const parsed = this.parseComment(comment);
    combineLatest([
      this.conversationId$,
      this.organizationService.getCurrentOrganization(),
      this.context$,
      this.contextId$,
    ])
      .pipe(first())
      .subscribe(([conversationId, organizationId, context, contextId]) => {
        const conversationMessageId = cuid();
        this.commentsCreatedByMe.push(conversationMessageId);
        this.insertCommentGQL
          .mutate({
            ...{
              update: (cache, mutationResult) => {
                const variables: GetCommentsQueryVariables = {
                  context,
                  contextId,
                };
                const res = cloneDeep(
                  cache.readQuery({
                    query: GetCommentsDocument,
                    variables,
                  }) as GetCommentsQuery,
                );
                res.getComments.comments.push({
                  ...mutationResult.data.insertComment,
                });
                cache.writeQuery({
                  query: GetCommentsDocument,
                  variables,
                  data: res,
                });

                const mineCommentsVariables: CommentsByMeInMonthQueryVariables =
                  {
                    organizationId,
                    month: DateTime.now().toFormat('MM-y'),
                  };
                const mineComments = cloneDeep(
                  cache.readQuery({
                    query: CommentsByMeInMonthDocument,
                    variables: mineCommentsVariables,
                  }) as CommentsByMeInMonthQuery,
                );
                if (
                  mineComments &&
                  Array.isArray(mineComments.getMineCommnets)
                ) {
                  const standaloneComment = {
                    ...mutationResult.data.insertComment,
                    context: this._context,
                    contextId: this._contextId,
                    __typename: 'StandAloneComment' as const,
                  };
                  mineComments.getMineCommnets.push(standaloneComment);
                  cache.writeQuery({
                    query: CommentsByMeInMonthDocument,
                    variables: mineCommentsVariables,
                    data: mineComments,
                  });
                }
              },
            },
            variables: {
              comment,
              type: CommnetType.Text,
              conversationId,
              conversationMessageId,
              contextId,
              context,
              organizationId,
              mentionedMembers: parsed.mentions,
            },
          })
          .pipe(first())
          .subscribe();
      });
  }

  updateLastSeen(lastSeenCommentDate: Array<number>) {
    this.conversationStatus$
      .pipe(
        switchMap(({ userHasConversationId }) =>
          this.updateSeenCommentsGQL.mutate({
            variables: {
              lastSeenCommentDate: Math.max(...lastSeenCommentDate),
              userHasConversationId,
              seenCommentsCount: lastSeenCommentDate.length,
            },
          }),
        ),
        first(),
      )
      .subscribe();
  }
}
